import { useEffect } from 'react';
import { keyboardCoreHooks } from './keyboardCoreHooks';
import { keyboardHooks } from './keyboardHooks';
import { mouseCoreHooks } from './mouseCoreHooks';
import { mouseHooks } from './mouseHooks';

// import DemoTheme from '../info/launcherTemplates/DemoTheme.json';
import { findNodePath } from '../functions/launcherElement/findNodePath';
import Launcher1Theme from '../info/launcherTemplates/Launcher1Theme.json';
import { elementHandleHooks } from './elementHandleHooks';
import { sessionHooks } from './sessionHooks';
import { LauncherElementData, useLauncherElementsStore } from './stores/launcherElementsStore';
import { LauncherWindowData, useLauncherWindowStore } from './stores/launcherWindowStore';
import { UIData, useUIStore } from './stores/uiStore';
import { LauncherStatesData, useLauncherStatesStore } from './stores/launcherStatesStore';
import { getStateView } from '../functions/launcherConfig/getStateView';
import { compareObjects } from '../functions/utils/comapareObjects';
import { useScene } from '../context/sceneContext';

// This component helps to avoid unnecessary rerenders

export const Hooks = () => {
  const [UIData, setUIData] = useUIStore<UIData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [launcherStatesData] = useLauncherStatesStore<LauncherStatesData>();
  const scene = useScene();
  mouseCoreHooks();
  keyboardCoreHooks();

  mouseHooks();
  keyboardHooks();

  elementHandleHooks();

  sessionHooks();

  useEffect(() => {
    // Import config on app start
    if (!scene) return;

    const config = Launcher1Theme;
    const configJSON = JSON.stringify(config);

    scene.importConfig(configJSON, true);

    // Hide sidebar if browser window is narrow
    if (window.innerWidth < 1000) {
      setUIData({ isSidebarOpen: false });
    }
  }, []);

  // Update current state view when current launcher state changes
  useEffect(() => {
    if (compareObjects(UIData.currentLauncherState, [null, null])) return;
    if (compareObjects(UIData.currentLauncherState, ['isAnyTaskInProgress', 'true'])) return;
    if (compareObjects(UIData.currentLauncherState, ['isAnyTaskPaused', 'true'])) return;
    if (compareObjects(UIData.currentLauncherState, ['hasAnyTaskFailed', 'true'])) return;

    const stateView = getStateView(UIData.currentLauncherState);
    setUIData({ currentStateView: stateView || 'stateViewNotInstalled' });
  }, [UIData.currentLauncherState, launcherStatesData]);

  // Reset pseudoclass when selected element changes
  useEffect(() => {
    setUIData({ pseudoclass: undefined });
  }, [UIData.selectedElementIDs]);

  // Reset side state when simulation mode is toggled
  useEffect(() => {
    if (!scene || UIData.editorMode === 'editing') return;

    scene.unhoverAllElements();
    scene.deselectAllElements();
    setUIData({ currentLauncherState: [null, null] });
  }, [UIData.editorMode === 'simulation']);

  // Save history on elements change
  useEffect(() => {
    const debouncingTimeMs = 300;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    timeoutId = setTimeout(() => {
      scene.historySave();

      timeoutId = null;
    }, debouncingTimeMs);

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [launcherElementsData, launcherWindowData]);

  // Show launcher popup on select
  useEffect(() => {
    if (!scene || !UIData.selectedElementIDs.length) {
      setUIData({ openPopupIDs: [] });
      return;
    }

    const popupRelatedElements = UIData.selectedElementIDs
      .map((id) => {
        let popupID = undefined;
        const nodePath = findNodePath(launcherWindowData.children, id);
        nodePath.forEach((nodeID) => {
          const { type } = scene.getElementData(nodeID, { type: undefined }, '');

          if (type === 'popup') {
            popupID = nodeID;
            return true;
          }
        });

        return popupID;
      })
      .filter((id) => id !== undefined) as unknown as string[];

    setUIData({ openPopupIDs: popupRelatedElements });
  }, [UIData.selectedElementIDs]);

  return <></>;
};
